<template>
  <v-ons-page id="downloads">
    <ons-toolbar class="toolbar-fake"></ons-toolbar>
    <v-ons-progress-bar indeterminate class="app-bar-progress" v-show="loading"></v-ons-progress-bar>
    <top-pull-hook :action="reload" />
    <main role="main">
      <h1 class="page-title">{{ $t("downloads.title") }}</h1>
      <div class="justify-row page-info">
        <div class="main-item small">
          <div v-if="!!lastSearchKeywords">{{ $t("downloads.search_result_count", { 'count': episodes.length, 'keyword': lastSearchKeywords }) }}</div>
          <div v-else>{{ $tc("plural.episode", episodes.length) }}</div>
          <div class="storage">
            <div class="storage-used" v-bind:style="{ width: '' + usedStoragePercentage + '%' }"></div>
          </div>
          {{ $t("downloads.approx") }} <strong class="space-used">{{ usedStorageSize !== null ? usedStorageSize : '??' }} MB</strong> {{ $t("downloads.used") }} / <strong class="space-available">{{ availableStorageSize || '?.?' }} GB</strong> {{ $t("downloads.available") }}
        </div>
        <div>
          <ons-button class="btn-outline" modifier="small alert"
            :disabled="!$isCordova"
            @click="deleteAll"
          >{{ $t("downloads.delete_all") }}</ons-button>
        </div>
      </div>
      <div class="search-box">
        <v-ons-search-input :placeholder="$t('downloads.search')" modifier="full" value="" v-model="keywords" :disabled="!$isCordova" @input="debouncedSearch"></v-ons-search-input>
      </div>
      <div style="padding: 20px;" v-if="!$isCordova">
        <p style="width: 360px; display: block; margin-left: auto; margin-right: auto;">{{ $t("downloads.mobile_app_only") }}</p>
        <a href="https://play.google.com/store/apps/details?id=app.edifi.cordova" target="_blank">
          <img style="width: 180px; display: block; margin-left: auto; margin-right: auto;" class="non-ios-only" src="@/assets/img/playstore.png" alt="Get Edifi On Google Play">
        </a>
        <a href="https://apps.apple.com/us/app/edifi-app/id1493412618?ls=1" target="_blank">
          <img style="width: 180px; display: block; margin-left: auto; margin-right: auto;" class="non-android-only" src="@/assets/img/appstore.png" alt="Download Edifi On the App Store">
        </a>
      </div>
      <section
        v-for="published_date in Object.keys(episodesGroupByDate).sort((a, b) => b-a)"
        :key="published_date"
        v-else
      >
        <h2 class="timeline-date"><time :datetime="timestampToDate(published_date)">{{ published_date | timestampHumanizeDate }}</time></h2>
        <div class="container">
          <episode-list-detailed :mediaLeft="true" :episodes="episodesGroupByDate[published_date]" :show-delete="true" />
        </div>
      </section>
    </main>
    <float-player />
  </v-ons-page>
</template>

<script>
import Fuse from 'fuse.js';
import { debounce } from "debounce";
import TopPullHook from '@/components/TopPullHook.vue';
import EpisodeListDetailed from '@/components/EpisodeListDetailed.vue';
import FloatPlayer from '@/components/FloatPlayer.vue';
import Episode from '@/models/Episode';
import DownloadHelper from '@/mixins/DownloadHelper';

export default {
  name: 'downloads',
  mixins: [DownloadHelper],
  components: {
    'top-pull-hook': TopPullHook,
    'episode-list-detailed': EpisodeListDetailed,
    'float-player': FloatPlayer,
  },

  data() {
    return {
      keywords: null,
      lastSearchKeywords: null,
      availableStorageSize: null,
      loading: false,
    };
  },

  computed: {
    keywordsIsValid() {
      return this.keywords === null || this.keywords === '' || this.keywords.replace(/[&\/\\#,+()$~%.'":*?<>{}@ ]/g, '').length >= 2;
    },
    episodes() {
      if (!this.lastSearchKeywords || this.lastSearchKeywords === '') {
        return this.allEpisodes;
      }
      return this.fuse.search(this.lastSearchKeywords);
    },
    allEpisodes() {
      return Episode.query().where('_download_status', value => value !== 'IDLE').with('channel').orderBy('published_at', 'desc').get();
    },
    fuse() {
      return new Fuse(this.allEpisodes, { findAllMatches: true, keys: ['title', 'channel.title'] });
    },
    usedStorageSize() {
      return this.$isCordova ? 29 * this.allEpisodes.length : null;
    },
    usedStoragePercentage() { // 0 - 100
      if (!this.availableStorageSize) { return 20; }
      if (this.usedStorageSize === null) { return 20; }
      return 100 * this.usedStorageSize / (this.usedStorageSize + this.availableStorageSize * 1024);
    },
    episodesGroupByDate() {
      let offset = (new Date()).getTimezoneOffset() * 60;
      return this.episodes.reduce(function (r, episode) {
        let published_date = Math.floor((episode.published_at - offset)/86400) * 86400 + offset;
        r[published_date] = r[published_date] || [];
        r[published_date].push(episode);
        return r;
      }, {});
    },
  },

  methods: {
    reload(done) {
      if (!window.cordova) {
        if (done) { done(); }
        return;
      }
      if (!this.keywordsIsValid) {
        if (done) { done(); }
        return;
      }
      let that = this;

      this.loading = true;
      this.lastSearchKeywords = this.keywords;
      cordova.exec((result) => {
        let denominator = 1024 * 1024;
        if (this.$ons.platform.isIOS() || this.$ons.platform.isIPadOS()) {
          denominator = denominator * 1024;
        }
        this.availableStorageSize = (result / denominator).toFixed(1);
      }, (error) => {
        console.log(error);
      }, 'File', 'getFreeDiskSpace', []);
      Episode.fetchAllDownloadedEpisodes().then(() => {
        that.updateDownloadsBadge();
      }).catch((err) => {
        console.log(err);
      }).finally(() => {
        this.loading = false;
        if (done) { done(); }
      });
    },
    search() {
      this.reload();
    },
    deleteAll() {
      if (!window.cordova) { return; }
      this.$ons.notification.confirm(this.$t('messages.delete_downloads_confirm')).then((response) => {
        if (!response) { return; }
        Episode.query().where('_download_status', value => value !== 'IDLE').get().forEach((episode) => {
          episode.delete();
        });
        this.updateDownloadsBadge();
      });
    },
  },

  created() {
    this.debouncedSearch = debounce(() => {
      this.search();
    }, 1000);
    this.reload();
  },
};
</script>
